/* Branding: colour schemes, the logo, and the business name.
 *
 * Everything the board draws comes from CSS custom properties, so a theme is
 * just a set of values written onto <html>. Five presets ship; any of the six
 * major colours can be overridden with a hex on top of whichever preset is
 * selected.
 *
 * Shades that have to stay legible — text on the header, the badge fills, the
 * skipped column — are DERIVED here rather than stored, so they follow a custom
 * colour instead of being left behind by it. The text on the header flips
 * between white and near-black on the header's luminance, which is what stops a
 * pale custom primary from rendering white-on-white.
 *
 * Stored in the salon's settings, not on the device: what the console is called
 * and what it looks like is one answer for the whole shop, and setting it up on
 * the desk tablet only to find the wall screen still on the default was never
 * the point. Every station keeps a mirror of it, so the theme is applied from
 * localStorage before the first paint and a station with no internet still comes
 * up branded. See js/prefs.js.
 */
import { prefs } from './prefs.js';

// Every preset keeps a light surface. A true dark mode would need more than a
// palette swap — the squares, the fraction slashes and the missed hatching all
// assume a light cell — so it is deliberately not offered here.
var PRESETS = [
  {
    id: 'navy', name: 'Navy & Gold', note: 'The original palette — deep navy, warm gold.',
    colors: {
      bg: '#F8F9FA', surface: '#FFFFFF', primary: '#203A5C', accent: '#C5A059',
      text: '#333333', muted: '#6B7480', line: '#E3E7EC', lineSoft: '#EEF1F5',
      blue: '#2E6FD9', danger: '#B3453C', action: '#EDF1F7'
    }
  },
  {
    id: 'slate', name: 'Slate & Teal', note: 'Cooler and greyer, with a teal accent.',
    colors: {
      bg: '#F3F5F7', surface: '#FFFFFF', primary: '#2F3A45', accent: '#3E9E96',
      text: '#2B3138', muted: '#6A7480', line: '#E1E6EA', lineSoft: '#EDF0F3',
      blue: '#2E7FD9', danger: '#B3453C', action: '#E8EDF1'
    }
  },
  {
    id: 'plum', name: 'Plum & Rose', note: 'Warmer, softer, a spa-room palette.',
    colors: {
      bg: '#FAF6FA', surface: '#FFFFFF', primary: '#4A2B4D', accent: '#C07C97',
      text: '#33262F', muted: '#756B72', line: '#EAE0E8', lineSoft: '#F4EDF3',
      blue: '#7A5AD9', danger: '#B3453C', action: '#F1E8F0'
    }
  },
  {
    id: 'forest', name: 'Forest & Brass', note: 'Deep green with a brass accent.',
    colors: {
      bg: '#F4F8F4', surface: '#FFFFFF', primary: '#24453A', accent: '#C0964A',
      text: '#26302A', muted: '#68756D', line: '#DFE8E1', lineSoft: '#EDF3EE',
      blue: '#2E8F6F', danger: '#B3453C', action: '#E7EFE8'
    }
  },
  {
    id: 'ink', name: 'Ink & Amber', note: 'Highest contrast — best on a bright floor.',
    colors: {
      bg: '#ECEEF1', surface: '#FFFFFF', primary: '#1E2124', accent: '#E0A32E',
      text: '#16181B', muted: '#5C646D', line: '#D8DDE3', lineSoft: '#E8ECF0',
      blue: '#1F6FEB', danger: '#A8342B', action: '#DEE3E9'
    }
  }
];

// The six a hex can be typed into. The rest of a preset (muted, lines, the
// blue suggestion outline, the danger red) stays as the preset defines it —
// those are signals rather than branding, and letting them drift breaks the
// meaning of an outline more than it expresses a taste.
var FIELDS = [
  { key: 'primary', label: 'Headers & top bar',
    hint: 'The bar across the top and the staff name row. Keep it dark: the text on it flips between white and black on its own, but a mid-tone reads poorly either way.' },
  { key: 'action', label: 'Quick add buttons',
    hint: 'Fill behind the quick add buttons. Its whole job is to differ from the squares below it.' },
  { key: 'accent', label: 'Accent',
    hint: 'Request stars, the half-turn badge, the ten-minute warning.' },
  { key: 'bg', label: 'Page background', hint: 'Behind everything.' },
  { key: 'surface', label: 'Cards & squares', hint: 'The turn squares, panes and modals.' },
  { key: 'text', label: 'Text', hint: 'Body text on light surfaces.' }
];

var CSS_VAR = {
  bg: '--off-white', surface: '--white', primary: '--navy', accent: '--gold',
  text: '--text', muted: '--muted', line: '--line', lineSoft: '--line-soft',
  blue: '--blue', danger: '--danger', action: '--action'
};

/* ---------- colour maths ---------- */

// Returns #rrggbb, or null if it is not a colour we can compute with. Accepts
// a bare "203a5c" as well, because that is how people paste them.
function clean(value) {
  if (typeof value !== 'string') return null;
  var v = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(v)) {
    v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
  }
  return /^[0-9a-f]{6}$/i.test(v) ? '#' + v.toLowerCase() : null;
}

function toRgb(hex) {
  var v = clean(hex) || '#000000';
  return {
    r: parseInt(v.slice(1, 3), 16),
    g: parseInt(v.slice(3, 5), 16),
    b: parseInt(v.slice(5, 7), 16)
  };
}

function toHex(c) {
  function p(n) {
    return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  }
  return '#' + p(c.r) + p(c.g) + p(c.b);
}

// t = 0 keeps a, t = 1 becomes b.
function mix(a, b, t) {
  var x = toRgb(a), y = toRgb(b);
  return toHex({
    r: x.r + (y.r - x.r) * t,
    g: x.g + (y.g - x.g) * t,
    b: x.b + (y.b - x.b) * t
  });
}

function rgba(hex, alpha) {
  var c = toRgb(hex);
  return 'rgba(' + c.r + ', ' + c.g + ', ' + c.b + ', ' + alpha + ')';
}

// Relative luminance, good enough to decide black text or white.
function luminance(hex) {
  var c = toRgb(hex);
  return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
}

/* ---------- resolve + apply ---------- */

function preset(id) {
  return PRESETS.find(function (p) { return p.id === id; }) || PRESETS[0];
}

// Preset values with any custom hexes laid over the top.
function resolve(state) {
  var out = Object.assign({}, preset(state.preset).colors);
  var custom = state.custom || {};
  FIELDS.forEach(function (f) {
    var v = clean(custom[f.key]);
    if (v) out[f.key] = v;
  });
  return out;
}

function apply(state) {
  var c = resolve(state);
  var el = document.documentElement;

  Object.keys(CSS_VAR).forEach(function (k) {
    if (c[k]) el.style.setProperty(CSS_VAR[k], c[k]);
  });

  // Whatever sits on the header has to read against it, so it is chosen from
  // the header's own luminance rather than assumed white.
  var on = luminance(c.primary) > 0.55 ? '#14181e' : '#ffffff';
  el.style.setProperty('--on-primary', on);
  el.style.setProperty('--on-primary-soft', rgba(on, 0.68));
  el.style.setProperty('--on-primary-faint', rgba(on, 0.17));
  el.style.setProperty('--on-primary-grip', rgba(on, 0.5));

  el.style.setProperty('--dark-navy', mix(c.primary, '#000000', 0.32));

  // Badges and warnings sit on the header, so they are tinted toward it and
  // lifted back toward the readable side.
  el.style.setProperty('--badge-busy', rgba(c.accent, 0.42));
  el.style.setProperty('--badge-half', rgba(c.blue, 0.45));
  el.style.setProperty('--badge-skip', rgba(on, 0.2));
  // Somebody whose hours have run out. Fainter than the skip badge on purpose:
  // a bypass is a thing somebody has just done and worth catching the eye,
  // and this is a time that was set this morning and has simply arrived.
  el.style.setProperty('--badge-gone', rgba(on, 0.12));
  el.style.setProperty('--clock-soon', mix(c.accent, on, 0.4));
  el.style.setProperty('--clock-due', mix(c.danger, on, 0.45));
  el.style.setProperty('--head-skip', mix(c.primary, c.danger, 0.55));
  el.style.setProperty('--head-gone', mix(c.primary, '#000000', 0.22));

  el.style.setProperty('--action-line', mix(c.action, c.text, 0.18));

  // Tints of the two signal colours. Without these the faint washes behind a
  // suggested square stay blue while a theme's outline has gone green.
  el.style.setProperty('--primary-ring', rgba(c.primary, 0.18));
  el.style.setProperty('--blue-soft', rgba(c.blue, 0.09));
  el.style.setProperty('--blue-line', rgba(c.blue, 0.25));
}

/* ---------- logo ---------- */

// Held as a data URI rather than as a file anywhere: it has to survive the
// salon's internet dropping, which rules out hosting it. That is also what
// sets the size cap — every station keeps a mirror of the settings in
// localStorage, the whole origin gets about 5 MB, and base64 inflates a file
// by a third, so a 200 KB logo costs ~270 KB of that.
var LOGO = {
  maxBytes: 200 * 1024,
  // No SVG: it can carry script and external references, and it has no
  // reliable intrinsic size to check against the limits below.
  types: ['image/png', 'image/jpeg', 'image/webp'],
  typeLabel: 'PNG, JPEG or WebP',
  maxW: 1200,
  maxH: 400,
  minH: 32,
  // Rendered height in the top bar; anything shorter than this is upscaled and
  // looks soft, which is what minH guards against.
  displayH: 34
};

function fileSizeLabel(bytes) {
  return bytes >= 1024 * 1024
    ? (bytes / 1024 / 1024).toFixed(1) + ' MB'
    : Math.round(bytes / 1024) + ' KB';
}

// Resolves { dataUrl, width, height }, or rejects with a message meant to be
// shown as-is. Every check names the actual value so the fix is obvious.
function readLogo(file) {
  return new Promise(function (resolve, reject) {
    if (!file) return reject(new Error('No file chosen.'));
    if (LOGO.types.indexOf(file.type) === -1) {
      return reject(new Error(
        'That is a ' + (file.type || 'unknown') + ' file — use ' + LOGO.typeLabel + '.'));
    }
    if (file.size > LOGO.maxBytes) {
      return reject(new Error(
        'That file is ' + fileSizeLabel(file.size) + '. The limit is ' +
        fileSizeLabel(LOGO.maxBytes) + ' — export it smaller and try again.'));
    }
    var reader = new FileReader();
    reader.onerror = function () { reject(new Error('That file could not be read.')); };
    reader.onload = function () {
      var dataUrl = String(reader.result || '');
      var img = new Image();
      img.onerror = function () { reject(new Error('That file is not a readable image.')); };
      img.onload = function () {
        var w = img.naturalWidth, h = img.naturalHeight;
        if (!w || !h) return reject(new Error('That image has no size.'));
        if (w > LOGO.maxW || h > LOGO.maxH) {
          return reject(new Error(
            'That image is ' + w + '×' + h + '. The limit is ' +
            LOGO.maxW + '×' + LOGO.maxH + '.'));
        }
        if (h < LOGO.minH) {
          return reject(new Error(
            'That image is only ' + h + 'px tall. It needs at least ' + LOGO.minH +
            'px or it will look soft in the bar.'));
        }
        resolve({ dataUrl: dataUrl, width: w, height: h });
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

function loadLogo() { return prefs.get('logo') || null; }

// Returns a message when the device could not keep its mirror — quota, which
// only the logo is ever big enough to hit. The setting still went up; it is
// the offline copy that did not fit, and silently dropping that would look
// like the upload worked until the next reload with no internet.
function saveLogo(logo) {
  var warn = prefs.set('logo', logo || null);
  return warn ? 'There was no room to keep that on this device — use a smaller file.' : null;
}

/* ---------- business name ---------- */

// Kept beside the logo because it is the same decision: what the top bar says
// this console belongs to. Nothing in the app hard-codes a business — an
// unnamed install falls back to FALLBACK rather than to anyone's name.
var BRAND = { maxLen: 40, fallback: 'Console' };

function cleanBrand(value) {
  return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, BRAND.maxLen);
}

function loadBrand() { return cleanBrand(prefs.get('brand')); }

function saveBrand(name) {
  var v = cleanBrand(name);
  prefs.set('brand', v);
  return v;
}

/* ---------- storage ----------
 *
 * Everything here reads and writes through prefs, which owns both the salon's
 * copy and this device's mirror of it. This used to be looked up on each call,
 * through a helper, so that nothing here depended on script order. The import
 * says the same thing and the module graph enforces it.
 */

function load() {
  var state = { preset: PRESETS[0].id, custom: {} };
  try {
    var saved = prefs.get('theme');
    if (saved && typeof saved === 'object') {
      // 'dream' was this preset's id before it was renamed for the business
      // name setting. Dropping it would silently reset a station's scheme.
      if (saved.preset === 'dream') saved.preset = 'navy';
      if (saved.preset && preset(saved.preset).id === saved.preset) {
        state.preset = saved.preset;
      }
      if (saved.custom && typeof saved.custom === 'object') {
        FIELDS.forEach(function (f) {
          var v = clean(saved.custom[f.key]);
          if (v) state.custom[f.key] = v;
        });
      }
    }
  } catch (e) { /* corrupt or unavailable — the default is fine */ }
  return state;
}

function save(state) {
  prefs.set('theme', { preset: state.preset, custom: Object.assign({}, state.custom) });
}

export const theme = {
  PRESETS: PRESETS,
  FIELDS: FIELDS,
  LOGO: LOGO,
  BRAND: BRAND,
  cleanBrand: cleanBrand,
  loadBrand: loadBrand,
  saveBrand: saveBrand,
  clean: clean,
  resolve: resolve,
  apply: apply,
  load: load,
  save: save,
  readLogo: readLogo,
  loadLogo: loadLogo,
  saveLogo: saveLogo,
  fileSizeLabel: fileSizeLabel
};

// Applied before the app mounts so the board never flashes the default first.
// This module is evaluated ahead of the shell because everything that renders
// imports it, directly or through the store — which is the same guarantee the
// old script order gave, now enforced rather than arranged.
theme.applyStored = function () {
  var state = load();
  apply(state);
  return state;
};
theme.applyStored();
